import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useTheme } from '../contexts/ThemeContext';
import { primaryNavigation, moreNavigation } from '../lib/navigation';
import { navigationIdentity, navigationStyle, navigationOnPath } from '../lib/navigationIdentity';
import MoreMenu from './MoreMenu';
import './Navigation.css';

const MORE = { ...navigationIdentity('/more'), more: true, key: 'nav.more', testid: 'nav-more' };

export default function BottomNav() {
    const { t, locale } = useI18n();
    const { experience } = useTheme();
    const { pathname } = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);
    const [extraClearance, setExtraClearance] = useState(0);
    const nav = useRef(null);
    const closeMore = useCallback(() => setMoreOpen(false), []);

    // Doctor and Snap are neighbours; secondary and account tools live in More.
    const links = primaryNavigation(experience);
    const slots = [...links, MORE];
    const moreActive = moreNavigation(experience).some(group => group.items.some(({ to }) => navigationOnPath(pathname, to)));
    const activeIdx = slots.findIndex(slot => (slot.more ? moreActive : navigationOnPath(pathname, slot.to)));
    const active = slots[activeIdx];
    const width = 100 / slots.length;

    useEffect(() => {
        // Pages reserve room for the old dock height. Add only the excess for
        // wrapped translations, larger fonts and the phone's bottom inset.
        const measure = () => setExtraClearance(Math.max(0, Math.ceil(nav.current?.getBoundingClientRect().height || 0) - 96));
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (nav.current) observer?.observe(nav.current);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [locale]);

    return (
        <>
            <MoreMenu open={moreOpen} onClose={closeMore} />
            <nav
                ref={nav}
                data-testid="bottom-nav"
                aria-label={t('nav.main_label')}
                className="gd-bottom-nav fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-2xl px-3 pb-nav-safe"
            >
                <div className="nav-holo relative backdrop-blur-md backdrop-saturate-150 bg-brand-bg/75 border border-brand-border/70 rounded-[22px] shadow-[0_-2px_24px_-12px_rgba(27,38,28,0.18),0_12px_32px_-16px_rgba(27,38,28,0.35)] px-1 py-1 flex items-center justify-between">
                    {/* Liquid indicator that springs between tabs */}
                    <span
                        aria-hidden="true"
                        data-testid="nav-indicator"
                        data-index={activeIdx}
                        className="nav-indicator"
                        data-nav-function={active?.id}
                        style={{
                            ...navigationStyle(active?.to || '/more'),
                            // Items fill the content box (the container has 4px padding each side)
                            left: `calc(4px + (100% - 8px) * ${(activeIdx < 0 ? 0 : activeIdx) * width / 100})`,
                            width: `calc((100% - 8px) * ${width / 100})`,
                            opacity: active ? 1 : 0,
                        }}
                    />
                    {slots.map(({ to, more, icon: Icon, key, testid, id }, i) => {
                        const isActive = i === activeIdx;
                        const inner = (
                            <>
                                <span className="gd-dock-glyph" aria-hidden="true"><Icon key={isActive ? 'on' : 'off'} size={18} strokeWidth={isActive ? 2 : 1.8} className={isActive ? 'nav-icon-pop' : ''} /></span>
                                <span className="gd-dock-label text-[10px] font-sans font-medium tracking-wide leading-tight text-center">{t(key)}</span>
                            </>
                        );
                        const cls = `gd-dock-link relative z-10 flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2 rounded-[18px] transition-colors duration-300${isActive ? ' is-active' : ''}`;
                        if (more) {
                            return (
                                <button
                                    key="more" type="button" data-testid={testid} data-active={moreActive}
                                    data-nav-function={id} style={navigationStyle('/more')}
                                    aria-expanded={moreOpen} aria-haspopup="dialog"
                                    aria-controls={moreOpen ? 'growdaily-more-menu' : undefined}
                                    onClick={() => setMoreOpen(true)} className={cls}
                                >
                                    {inner}
                                </button>
                            );
                        }
                        return (
                            <NavLink key={to} to={to} data-testid={testid} data-nav-function={id} style={navigationStyle(to)} className={cls}>
                                {inner}
                            </NavLink>
                        );
                    })}
                </div>
            </nav>
            <div aria-hidden="true" data-testid="nav-extra-clearance" style={{ height: extraClearance }} />
        </>
    );
}
